// utils/v3/summary.ts — table (POS bill) maths (SPEC §6)
// Ports legacy `utils/utilItems.ts` (utilsItemComputeSummary + utilsItemComputeOneTable)
// and `utils/items.ts` (useCombineItems).
import type { MoneyField, TableItem, TableSummary } from '~/types/v3/entities'

export type { TableSummary }

export interface CombinedTableItem extends TableItem {
  key: string
  subTotal: number
}

/** Legacy convention: money fields may arrive as `[gross, ?, net]`; the net is index [2]. */
export function moneyOf(value: MoneyField | unknown): number {
  if (Array.isArray(value)) return Number(value[2]) || 0
  return Number(value) || 0
}

const round2 = (n: number): number => Math.round(n * 100) / 100

function sumChain(items: readonly TableItem[], key: string): number {
  let sum = 0
  for (const item of items) sum += moneyOf(item?.[key])
  return sum
}

/** Legacy __rounding: pull the total to the nearest whole baht. */
function computeRounding(sumBeforeRounding: number): number {
  const decimal = sumBeforeRounding - Math.floor(sumBeforeRounding)
  return decimal >= 0.5 ? 1 - decimal : -decimal
}

/**
 * Bill summary for a table.
 * subTotal/dc1/sc/dc2/tax are the raw sums (as legacy returns them);
 * `rounding` is derived from the 2-decimal-rounded components and
 * `grandTotal = round2(sumBeforeRounding + rounding)` (utilsItemComputeOneTable).
 */
export function computeTableSummary(items: readonly TableItem[] | null | undefined): TableSummary {
  const list = items ?? []

  const beforeRounding = [
    sumChain(list, 'extendedPrice'),
    sumChain(list, 'dc1'),
    sumChain(list, 'sc'),
    sumChain(list, 'dc2'),
    sumChain(list, 'tax'),
  ].map(round2)

  const sumBeforeRounding = beforeRounding.reduce((a, b) => a + b, 0)
  const rounding = round2(computeRounding(sumBeforeRounding))

  return {
    subTotal: sumChain(list, 'extendedPrice'),
    dc1: sumChain(list, 'dc1'),
    sc: sumChain(list, 'sc'),
    dc2: sumChain(list, 'dc2'),
    tax: sumChain(list, 'tax'),
    rounding,
    grandTotal: round2(sumBeforeRounding + rounding),
  }
}

/**
 * `${productId}:${comments}:${price}:${dc1}:${options2 productIds joined by ':'}`
 * (legacy `utils/items.ts:genKey` — when there are no options2 the tail is the
 * literal `undefined`, kept as-is so merging behaves identically).
 */
export function combineKey(item: TableItem): string {
  const optionKey = item?.options2?.map((option) => `${option.productId}`).join(':')
  const dc1 = item?.dc1 === undefined || item?.dc1 === null ? '' : String(item.dc1)
  return `${item.productId}:${item.comments}:${item.price}:${dc1}:${optionKey}`
}

/**
 * Merge identical table lines (qty + subTotal), ordered oldest first — same as
 * the POS bill. Lines sharing a timestamp (sent in one order) keep the order the
 * backend stored them in, so the guest and the POS read identically.
 */
export function combineTableItems(items: readonly TableItem[] | null | undefined): CombinedTableItem[] {
  const result: CombinedTableItem[] = []

  for (const item of items ?? []) {
    const key = combineKey(item)
    const index = result.findIndex((i) => i.key === key)
    if (index > -1) {
      result[index].qty += Number(item.qty) || 0
      result[index].subTotal += moneyOf(item.subTotal)
      continue
    }
    result.push({ ...item, key, subTotal: moneyOf(item.subTotal) } as CombinedTableItem)
  }

  // timestamp asc, ties (and unparsable timestamps) keep the backend's order
  return result
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const ta = new Date(a.item.timestamp ?? '').getTime()
      const tb = new Date(b.item.timestamp ?? '').getTime()
      if (!Number.isNaN(ta) && !Number.isNaN(tb) && ta !== tb) return ta - tb
      return a.index - b.index
    })
    .map((e) => e.item)
}
